import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest, debounceTime, distinctUntilChanged, map, startWith } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BlogDocsService, BlogPostMeta } from '../../shared/blog-docs.service';
import { CtaSectionComponent } from '../../components/cta-section/cta-section.component';
import { BlogCoverComponent } from '../../components/blog-cover/blog-cover.component';

interface BlogListV2Vm {
  allPosts: BlogPostMeta[];
  latestPost: BlogPostMeta | null;
  query: string;
  searchResults: BlogPostMeta[] | null;
}

@Component({
  selector: 'app-blog-list-v2',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CtaSectionComponent, BlogCoverComponent],
  templateUrl: './blog-list-v2.component.html',
  styleUrls: ['./blog-list-v2.component.scss'],
})
export class BlogListV2Component {
  private readonly destroyRef = inject(DestroyRef);
  private readonly docs = inject(BlogDocsService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly searchControl = new FormControl<string>('', { nonNullable: true });

  readonly vm$ = combineLatest([
    this.docs.getAllPosts(),
    this.route.queryParamMap.pipe(map((q) => ({ q: (q.get('q') || '').trim() }))),
    this.searchControl.valueChanges.pipe(startWith(''), debounceTime(150), distinctUntilChanged()),
  ]).pipe(
    map(([posts, qp, typed]) => {
      const query = (typed || qp.q || '').trim();
      const qLower = query.toLowerCase();

      const searchResults: BlogPostMeta[] | null = qLower
        ? posts.filter((p) => {
            const hay = `${p.title} ${p.slug} ${p.excerpt} ${(p.tagIds || []).join(' ')}`.toLowerCase();
            return hay.includes(qLower);
          })
        : null;

      return {
        allPosts: posts,
        latestPost: posts[0] ?? null,
        query,
        searchResults,
      } satisfies BlogListV2Vm;
    })
  );

  constructor() {
    // Sync search input ↔ URL query param
    this.searchControl.valueChanges
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        void this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { q: (value || '').trim() || null },
          queryParamsHandling: 'merge',
        });
      });

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((q) => {
      const fromUrl = (q.get('q') || '').trim();
      if (fromUrl !== this.searchControl.value) this.searchControl.setValue(fromUrl, { emitEvent: false });
    });
  }

  open(post: BlogPostMeta): void {
    window.location.href = `/blog/${post.slug}`;
  }

  clearSearch(): void {
    this.searchControl.setValue('');
  }

  trackBySlug(_: number, item: BlogPostMeta): string {
    return item.slug;
  }
}

